import { applyGlobalEstateFilters } from '@/lib/supabase/estates/global-filter';
import type { EstatesSelectBuilder } from '@/types';
import { atom, map, onSet } from 'nanostores';
import { z } from 'zod';
import globalData from '../globals';
import { OfferType } from '../supabase/estates/enums';

// Min and max values for the additional filters
export const minMaxValues = {
	rooms: {
		min: 1,
		max: 10,
	},
	area: {
		min: 0,
		max: 1_500,
	},
	price: {
		min: 0,
		max: 10_000_000,
	},
};

// Sort options
const sortOptionsSchema = z.union( [
	z.literal( 'priceDesc' ),
	z.literal( 'priceAsc' ),
	z.literal( 'areaDesc' ),
	z.literal( 'areaAsc' ),
	z.literal( 'roomsDesc' ),
	z.literal( 'roomsAsc' ),
	z.literal( 'newest' ),
] );
export type SortOption = z.infer< typeof sortOptionsSchema >;

// The interface for the filter store
export interface FilterStoreState {
	offerType: OfferType | null;
	estateTypes: string[];
	locations: string[];
	minRooms: number | null;
	maxRooms: number | null;
	minArea: number | null;
	maxArea: number | null;
	minPrice: number | null;
	maxPrice: number | null;
	sort: SortOption | null;
	newBuilding: boolean | null;
}

// The empty filter state that will be used when the user clicks the reset button
const emptyFilter: FilterStoreState = {
	offerType: null,
	estateTypes: [],
	locations: [],
	minRooms: minMaxValues.rooms.min,
	maxRooms: minMaxValues.rooms.max,
	minArea: minMaxValues.area.min,
	maxArea: minMaxValues.area.max,
	minPrice: minMaxValues.price.min,
	maxPrice: minMaxValues.price.max,
	sort: null,
	newBuilding: null,
};

// Make the initial filter state from the search params
function makeInitialFilter( predefinedFilters?: {
	offerType?: string;
	estateType?: string;
	location?: string;
	newBuilding?: boolean | null;
} ): FilterStoreState {
	const searchParams = new URLSearchParams( window.location.search );

	// Validate filters present in search params
	const offerTypeRaw =
		searchParams.get( 'offerType' ) || predefinedFilters?.offerType;
	const offerType = z
		.nativeEnum( OfferType )
		.nullable()
		.catch( null )
		.parse( offerTypeRaw );

	const estateTypesRaw =
		searchParams.get( 'estateTypes' ) || predefinedFilters?.estateType;
	const estateTypes = z
		.string()
		.min( 1 )
		.transform( ( v ) => v.split( ',' ) )
		.catch( [] )
		.parse( estateTypesRaw );

	const locationsRaw =
		searchParams.get( 'locations' ) || predefinedFilters?.location;
	const locations = z
		.string()
		.min( 1 )
		.transform( ( v ) => v.split( ',' ) )
		.catch( [] )
		.parse( locationsRaw );

	const minRoomsRaw = searchParams.get( 'minRooms' );
	const minRooms = z.coerce
		.number()
		.min( minMaxValues.rooms.min )
		.max( minMaxValues.rooms.max - 1 )
		.nullable()
		.catch( null )
		.parse( minRoomsRaw );

	const maxRoomsRaw = searchParams.get( 'maxRooms' );
	const maxRooms = z.coerce
		.number()
		.min( minMaxValues.rooms.min + 1 )
		.max( minMaxValues.rooms.max )
		.nullable()
		.catch( null )
		.parse( maxRoomsRaw );

	const minAreaRaw = searchParams.get( 'minArea' );
	const minArea = z.coerce
		.number()
		.min( minMaxValues.area.min )
		.max( minMaxValues.area.max )
		.nullable()
		.catch( null )
		.parse( minAreaRaw );

	const maxAreaRaw = searchParams.get( 'maxArea' );
	const maxArea = z.coerce
		.number()
		.min( minMaxValues.area.min )
		.max( minMaxValues.area.max )
		.nullable()
		.catch( null )
		.parse( maxAreaRaw );

	const minPriceRaw = searchParams.get( 'minPrice' );
	const minPrice = z.coerce
		.number()
		.min( minMaxValues.price.min )
		.max( minMaxValues.price.max )
		.nullable()
		.catch( null )
		.parse( minPriceRaw );

	const maxPriceRaw = searchParams.get( 'maxPrice' );
	const maxPrice = z.coerce
		.number()
		.min( minMaxValues.price.min )
		.max( minMaxValues.price.max )
		.nullable()
		.catch( null )
		.parse( maxPriceRaw );

	const sortRaw = searchParams.get( 'sort' );
	const sort = sortOptionsSchema.nullable().catch( null ).parse( sortRaw );

	const newBuildingRaw = searchParams.get( 'newBuilding' );
	const newBuilding = z
		.preprocess( ( val ) => {
			if ( typeof val === 'string' ) {
				return val.toLowerCase() === 'true' ? true : null;
			}
			return null;
		}, z.coerce.boolean().nullable() )
		.catch( null )
		.parse( newBuildingRaw );

	const filter: FilterStoreState = {
		offerType,
		estateTypes,
		locations,
		minRooms: minRooms ?? minMaxValues.rooms.min,
		maxRooms: maxRooms ?? minMaxValues.rooms.max,
		minArea: minArea ?? minMaxValues.area.min,
		maxArea: maxArea ?? minMaxValues.area.max,
		minPrice: minPrice ?? minMaxValues.price.min,
		maxPrice: maxPrice ?? minMaxValues.price.max,
		sort,
		newBuilding: predefinedFilters?.newBuilding ?? newBuilding,
	};

	return filter;
}

// The filter store
export const $filterStore = map< FilterStoreState >( makeInitialFilter() );

// Function to initialize filter store with predefined filters
export function initializeFilterStore( predefinedFilters?: {
	offerType?: string;
	estateType?: string;
	location?: string;
	newBuilding?: boolean | null;
} ) {
	const initialFilter = makeInitialFilter( predefinedFilters );
	$filterStore.set( initialFilter );
}

// Whether the URL search params and the query should be updated
export const $shouldUpdate = atom( true );

// When the filter store is set, update the URL search params
onSet( $filterStore, ( { newValue } ) => {
	if ( ! $shouldUpdate.get() ) {
		return;
	}

	// @ts-expect-error
	const query = new URLSearchParams( newValue );

	window.history.replaceState(
		null,
		'',
		`${ window.location.pathname }?${ query.toString() }`
	);
} );

// Reset the filter store to the empty state
export function resetFilter() {
	$filterStore.set( emptyFilter );
	window.history.replaceState( null, '', window.location.pathname );
}

// Apply the current set filters to the Supabase query
export function applyFiltersToQuery(
	query: EstatesSelectBuilder,
	filter: FilterStoreState
) {
	// Apply global gruppennummer filter first, if configured
	query = applyGlobalEstateFilters( query );
	const isSearchingForPlotsOnly =
		filter.estateTypes.length === 1 &&
		filter.estateTypes[ 0 ] === 'grundstueck';

	// Select only estates that are:
	// - published
	// - available
	// - have a WordPress URL
	query = query.eq( 'veroeffentlichen', true );
	query = query.not( 'wordpress_url', 'is', null );

	if ( globalData.config.isProjectPage ) {
		// Project sites only show estates flagged as "Nur Projektseite".
		// UNSICHTBAR auf Website is intentionally ignored on project sites.
		query = query.eq( 'nur_projektseite', true );
	} else {
		// Main site shows all visible estates (including project estates) and
		// excludes those marked as "UNSICHTBAR auf Website".
		query = query.eq( 'visible_on_website', true );
	}

	// Distribution Type
	if ( filter.offerType ) {
		query = query.eq( 'vermarktungsart', filter.offerType );
	}

	// Estate Type
	if ( filter.estateTypes.length > 0 ) {
		query = query.in( 'objektart', filter.estateTypes );
	}

	// New Building
	if ( filter.newBuilding !== null ) {
		query = query.eq( 'neubau', filter.newBuilding );
	}

	// City
	if ( filter.locations.length > 0 ) {
		query = query.in( 'ort', filter.locations );
	}

	// Rooms
	if ( filter.minRooms && ! isSearchingForPlotsOnly ) {
		// If users search for minimum 1 room, estates with less than 1 room should also be shown
		query = query.gte(
			'anzahl_zimmer',
			filter.minRooms === 1 ? 0 : filter.minRooms
		);
	}

	if (
		filter.maxRooms &&
		! isSearchingForPlotsOnly &&
		filter.maxRooms < minMaxValues.rooms.max
	) {
		query = query.lte( 'anzahl_zimmer', filter.maxRooms );
	}

	// Area
	if ( filter.minArea && ! isSearchingForPlotsOnly ) {
		query = query.gte( 'wohnflaeche', filter.minArea );
	}

	if (
		filter.maxArea &&
		! isSearchingForPlotsOnly &&
		filter.maxArea < minMaxValues.area.max
	) {
		query = query.lte( 'wohnflaeche', filter.maxArea );
	}

	// Price
	if ( ( filter.minPrice || filter.maxPrice ) && filter.offerType ) {
		const priceFieldsByOfferType: Record< OfferType, string > = {
			[ OfferType.Sale ]: 'kaufpreis',
			[ OfferType.Rent ]: 'kaltmiete',
		};

		const priceField = priceFieldsByOfferType[ filter.offerType ];

		if ( filter.minPrice && filter.maxPrice ) {
			// Both min and max: price must be within range
			query = query.gte( priceField, filter.minPrice );
			query = query.lte( priceField, filter.maxPrice );
		} else if ( filter.minPrice ) {
			// Only min: price must be >= min
			query = query.gte( priceField, filter.minPrice );
		} else if ( filter.maxPrice ) {
			// Only max: price must be <= max
			query = query.lte( priceField, filter.maxPrice );
		}
	}

	// Sorting
	if ( filter.sort ) {
		switch ( filter.sort ) {
			case 'priceDesc':
				query = query.order( 'kaufpreis', { ascending: false } );
				query = query.order( 'kaltmiete', { ascending: false } );
				break;
			case 'priceAsc':
				query = query.order( 'kaufpreis', { ascending: true } );
				query = query.order( 'kaltmiete', { ascending: true } );
				break;
			case 'areaDesc':
				query = query.order( 'wohnflaeche', { ascending: false } );
				break;
			case 'areaAsc':
				query = query.order( 'wohnflaeche', { ascending: true } );
				break;
			case 'roomsDesc':
				query = query.order( 'anzahl_zimmer', { ascending: false } );
				break;
			case 'roomsAsc':
				query = query.order( 'anzahl_zimmer', { ascending: true } );
				break;
			case 'newest':
				query = applyDefaultSorting( query );
				break;
		}
	} else {
		query = applyDefaultSorting( query );
	}

	if ( filter.estateTypes.length === 0 ) {
		query = query.not( 'duplikats_objekt', 'is', 'true' );
	}

	return query;
}

function applyDefaultSorting( query: EstatesSelectBuilder ) {
	query = query.order( 'new', {
		ascending: false,
	} );
	query = query.order( 'new_at', {
		ascending: false,
		nullsFirst: false,
	} );
	query = query.order( 'published_on_website_at', {
		ascending: false,
		nullsFirst: false,
	} );

	return query;
}
